export interface ReconnectController {
  schedule: (callback: () => void) => boolean;
  cancel: () => void;
  reset: () => void;
  getState: () => { attempts: number; nextDelayMs: number };
}

export interface Outbox<T> {
  enqueue: (item: T) => void;
  dequeue: () => T | undefined;
  clear: () => void;
  size: () => number;
}

export function createReconnectController(options: {
  baseDelayMs?: number;
  maxDelayMs?: number;
  maxAttempts?: number;
} = {}): ReconnectController {
  const baseDelayMs = options.baseDelayMs ?? 2000;
  const maxDelayMs = options.maxDelayMs ?? 8000;
  const maxAttempts = options.maxAttempts ?? 3;

  let attempts = 0;
  let timer: number | null = null;

  const cancel = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  return {
    schedule: (callback) => {
      cancel();

      if (attempts >= maxAttempts) {
        return false;
      }

      const delayMs = Math.min(baseDelayMs * 2 ** attempts, maxDelayMs);
      attempts += 1;
      timer = window.setTimeout(() => {
        timer = null;
        callback();
      }, delayMs);

      return true;
    },
    cancel,
    reset: () => {
      cancel();
      attempts = 0;
    },
    getState: () => ({
      attempts,
      nextDelayMs: attempts > 0 ? Math.min(baseDelayMs * 2 ** (attempts - 1), maxDelayMs) : 0,
    }),
  };
}

export function createOutbox<T>(): Outbox<T> {
  const queue: T[] = [];

  return {
    enqueue: (item) => {
      queue.push(item);
    },
    dequeue: () => queue.shift(),
    clear: () => {
      queue.length = 0;
    },
    size: () => queue.length,
  };
}
